import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";

const faqItems = [
  {
    q: "Do you charge for stocking and organization?",
    a: "Stocking and organization is a higher execution level than door delivery, and is priced accordingly.",
  },
  {
    q: "Do you require a minimum lead time?",
    a: "Yes. A 24-hour lead time is required.",
  },
  {
    q: "Can pricing be standardized across a portfolio?",
    a: "Yes. Bellhopt can support consistent pricing and rollout across multi-market portfolios.",
  },
  {
    q: "Are there hidden fees?",
    a: "No. Pricing should be presented transparently as groceries plus service and execution.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function PricingPage() {
  const title = "Bellhopt Pricing | Transparent Fees For Delivery And Stocking";
  const description =
    "Bellhopt pricing is transparent. Pay for groceries plus service and execution. Bellhopt Concierge delivers, stocks, and organizes with a 24-hour lead time.";
  const canonical = "https://bellhopt.com/pricing/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Transparent Pricing For Grocery Delivery And Stocking",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "Pricing", item: canonical },
      ],
    },
  ];

  return (
    <div className="w-full min-h-screen bg-[#F9FAFB] pt-[72px] font-segoe">
      <SeoHead
        title={title}
        description={description}
        canonical="/pricing/"
        robots="index,follow"
        ogTitle="Transparent Pricing"
        ogDescription="Clear pricing for grocery delivery, in-home stocking, and a concierge layer built for operators."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto py-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            Transparent Pricing For Grocery Delivery And Stocking
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            Bellhopt pricing is designed to be clear and predictable. You pay for groceries plus service and execution, delivered, stocked, and organized by Bellhopt Concierge.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started With Bellhopt</a>
            <Link to="/for/property-managers" className={SECONDARY_CTA}>For Property Managers</Link>
          </div>
        </section>

        <div className="mt-6 space-y-6">
          <Section id="how-pricing-works" title="How Pricing Works">
            <p>Bellhopt pricing is built from a few components:</p>
            <ol className="list-decimal pl-6 space-y-4 mt-4">
              <li>
                <strong>Grocery Total</strong>
                <p>The cost of items selected by the guest from over 20,000 products.</p>
              </li>
              <li>
                <strong>Service Fee</strong>
                <p>Covers coordination, guest support, and the operational layer required for consistent execution.</p>
              </li>
              <li>
                <strong>Delivery And Stocking Execution</strong>
                <p>Based on service level and access complexity. In-home stocking and organization requires access and additional handling.</p>
              </li>
              <li>
                <strong>Tip</strong>
                <p>Optional, based on your policy and checkout flow.</p>
              </li>
            </ol>
          </Section>

          <Section id="formula" title="Simple Total Formula">
            <div className="rounded-2xl border border-[#E5E7EB] p-5 bg-white/90 text-center">
              <p className="font-segoe font-semibold text-[24px] text-[#111827]">Total Cost = G + S + E + Tip</p>
            </div>
            <div className="mt-4">
              <p>Where:</p>
              <ul className="list-disc pl-6 mt-2">
                <li><strong>G</strong> = Grocery total (from 20,000+ products)</li>
                <li><strong>S</strong> = Service fee</li>
                <li><strong>E</strong> = Execution fee for delivery, stocking, and organization</li>
                <li><strong>Tip</strong> = optional</li>
              </ul>
            </div>
          </Section>

          <Section id="complexity" title="What Makes Pricing Higher Or Lower">
            <p className="font-segoe font-semibold text-[#111827]">Lower complexity:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>straightforward access</li>
              <li>smaller orders</li>
              <li>standard delivery</li>
            </ul>
            <p className="font-segoe font-semibold text-[#111827] mt-5">Higher complexity:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>in-home stocking and organization</li>
              <li>complex access requirements</li>
              <li>larger orders with more handling time</li>
            </ul>
          </Section>

          <Section id="portfolio-pricing" title="Portfolio Pricing For Property Managers">
            <p>
              For operators, the question is not just "price per order", it is rollout speed, consistency, and support impact.
            </p>
            <p className="mt-4">
              Bellhopt supports portfolios of any size, including 1,000+ homes. Enterprise pricing and rollout are structured commercially based on portfolio needs and operational scope.
            </p>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Ready To Get Started?
            </h2>
            <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Clear, predictable pricing for grocery delivery, stocking, and organization.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started</a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>Talk To Sales</a>
            </div>
          </section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">{item.q}</summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">Related Pages</h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/how-it-works">How it works</Link></li>
              <li><Link className="text-primary" to="/pre-arrival-groceries">Pre-arrival groceries</Link></li>
              <li><Link className="text-primary" to="/vacation-rental-grocery-delivery">Vacation rental grocery delivery</Link></li>
              <li><Link className="text-primary" to="/airbnb-grocery-stocking">Airbnb grocery stocking</Link></li>
              <li><Link className="text-primary" to="/for/property-managers">For property managers</Link></li>
              <li><Link className="text-primary" to="/integrations">Integrations</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  );
}

export default PricingPage;
